import { useState, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { GOAL_LABELS } from '../utils/dynamics';
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const COLUMNS = [
  { key: 'name',                label: 'Имя' },
  { key: 'goal',                label: 'Цель' },
  { key: 'last_weight',         label: 'Вес' },
  { key: 'last_waist',          label: 'Талия' },
  { key: 'last_measurement_date', label: 'Замер' },
  { key: 'measurement_count',   label: 'Кол-во' },
  { key: 'is_active',           label: 'Статус' },
];

export default function ClientsTable({ clients = [], onSelect }) {
  const { isDark, theme } = useTheme();
  const acc = theme.accent;
  const txt = isDark ? '#F2EEF9' : '#2D1B35';
  const mut = isDark ? 'rgba(200,185,220,.55)' : 'rgba(80,60,100,.5)';
  const bg  = isDark ? '#1A1526' : '#FFFFFF';
  const bdr = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';
  const hdr = isDark ? '#13101E' : '#F5F0FF';
  const gridColor = isDark ? '#2D2540' : '#E8D9F0';

  const [search, setSearch]   = useState('');
  const [statusF, setStatusF] = useState('active'); // active | all | inactive
  const [goalF, setGoalF]     = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const withName = useMemo(() => clients.map(c => ({
    ...c,
    name: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.username,
  })), [clients]);

  const filtered = useMemo(() => {
    let list = withName;
    if (statusF === 'active')   list = list.filter(c => c.is_active);
    if (statusF === 'inactive') list = list.filter(c => !c.is_active);
    if (goalF !== 'all')        list = list.filter(c => c.goal === goalF);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) || c.username.toLowerCase().includes(q)
      );
    }
    return list;
  }, [withName, statusF, goalF, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === 'last_measurement_date') { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; }
      if (sortKey === 'last_weight' || sortKey === 'last_waist' || sortKey === 'measurement_count') {
        va = va != null ? parseFloat(va) : -Infinity; vb = vb != null ? parseFloat(vb) : -Infinity;
      }
      if (sortKey === 'is_active') { va = va ? 1 : 0; vb = vb ? 1 : 0; }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const chartData = useMemo(() => {
    return sorted
      .filter(c => c.last_weight != null)
      .map(c => ({ name: c.name.split(' ')[0], weight: parseFloat(c.last_weight) }))
      .slice(0, 15);
  }, [sorted]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <ArrowUpDown size={12} style={{ opacity: .4 }} />;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const goalOptions = Object.entries(GOAL_LABELS);

  return (
    <div>
      <h1 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:800, fontSize:'1.5rem',
                   letterSpacing:-.5, margin:'0 0 18px' }}>Таблица клиентов</h1>

      {/* Фильтры */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
        <div style={{ position:'relative', flex:'1 1 180px' }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:mut }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Поиск по имени..."
            style={{ width:'100%', padding:'8px 10px 8px 30px', borderRadius:10, border:`1px solid ${bdr}`,
                     background:bg, color:txt, fontSize:'.85rem', outline:'none' }}
          />
        </div>
        <select value={statusF} onChange={e => setStatusF(e.target.value)}
          style={{ padding:'8px 10px', borderRadius:10, border:`1px solid ${bdr}`, background:bg, color:txt, fontSize:'.85rem' }}>
          <option value="active">Активные</option>
          <option value="all">Все</option>
          <option value="inactive">Заблокированные</option>
        </select>
        <select value={goalF} onChange={e => setGoalF(e.target.value)}
          style={{ padding:'8px 10px', borderRadius:10, border:`1px solid ${bdr}`, background:bg, color:txt, fontSize:'.85rem' }}>
          <option value="all">Все цели</option>
          {goalOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </div>

      {/* График сравнения весов */}
      {chartData.length >= 2 && (
        <div className="card p-4" style={{ marginBottom:16 }}>
          <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase',
                      letterSpacing:'.08em', margin:'0 0 10px' }}>Текущий вес по клиентам</p>
          <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 28)}>
            <BarChart data={chartData} layout="vertical" margin={{ top:5, right:20, left:10, bottom:5 }}>
              <CartesianGrid stroke={gridColor} horizontal={false} />
              <XAxis type="number" tick={{ fill: mut, fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={70} tick={{ fill: txt, fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${v} кг`, 'Вес']}
                contentStyle={{ background: isDark ? '#1A1525' : '#fff', border: `1px solid ${gridColor}`, borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="weight" radius={[0, 6, 6, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={acc} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Таблица */}
      <div style={{ borderRadius:16, overflow:'hidden', border:`1px solid ${bdr}`,
                    boxShadow:isDark?'0 2px 12px rgba(0,0,0,.3)':'0 2px 16px rgba(100,60,140,.08)' }}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
            <thead>
              <tr style={{ background:hdr }}>
                {COLUMNS.map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)}
                    style={{ padding:'10px 12px', textAlign:'left', color:mut, fontSize:'.7rem',
                             fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em',
                             borderBottom:`1px solid ${bdr}`, cursor:'pointer', whiteSpace:'nowrap',
                             userSelect:'none' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      {col.label} <SortIcon colKey={col.key} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, idx) => (
                <tr key={c.id} onClick={() => onSelect && onSelect(c.id)}
                  style={{ background: idx % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,.02)' : 'rgba(0,0,0,.018)'),
                           cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(232,0,111,.06)' : 'rgba(232,0,111,.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,.02)' : 'rgba(0,0,0,.018)')}>
                  <td style={{ padding:'10px 12px', color:txt, fontSize:'.85rem', fontWeight:600, borderBottom:`1px solid ${bdr}`, whiteSpace:'nowrap' }}>{c.name}</td>
                  <td style={{ padding:'10px 12px', color:mut, fontSize:'.8rem', borderBottom:`1px solid ${bdr}`, whiteSpace:'nowrap' }}>{GOAL_LABELS[c.goal] || '—'}</td>
                  <td style={{ padding:'10px 12px', color:txt, fontSize:'.85rem', borderBottom:`1px solid ${bdr}` }}>{c.last_weight != null ? `${parseFloat(c.last_weight)} кг` : '—'}</td>
                  <td style={{ padding:'10px 12px', color:txt, fontSize:'.85rem', borderBottom:`1px solid ${bdr}` }}>{c.last_waist != null ? `${parseFloat(c.last_waist)} см` : '—'}</td>
                  <td style={{ padding:'10px 12px', color:mut, fontSize:'.8rem', borderBottom:`1px solid ${bdr}`, whiteSpace:'nowrap' }}>
                    {c.last_measurement_date ? new Date(c.last_measurement_date).toLocaleDateString('ru-RU',{day:'numeric',month:'short'}) : '—'}
                  </td>
                  <td style={{ padding:'10px 12px', color:mut, fontSize:'.8rem', borderBottom:`1px solid ${bdr}`, textAlign:'center' }}>{c.measurement_count}</td>
                  <td style={{ padding:'10px 12px', fontSize:'.75rem', borderBottom:`1px solid ${bdr}` }}>
                    <span style={{ color: c.is_active ? '#22C55E' : '#EF4444', fontWeight:600 }}>
                      {c.is_active ? 'Активен' : 'Заблок.'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sorted.length === 0 && (
          <div style={{ padding:'2rem', textAlign:'center', color:mut }}>Никого не найдено</div>
        )}
      </div>
    </div>
  );
}
